/** Estimate monthly project cost: plan + paid add-ons + quota top-ups. */

import {
  effectiveQuotaFromStoreOption,
  findStoreQuotaOption,
  quotaAddonMonthlyAmount,
  quotaValueMode,
} from './quotaAddon.helpers';

function moduleName(catalog, code) {
  return catalog?.modules?.find((m) => m.code === code)?.name || code;
}

function buildSimulatedEntitlements(catalog, entitlements, cart) {
  const modules = { ...(entitlements?.modules || {}) };
  for (const code of cart?.modules || []) {
    modules[code] = true;
  }
  const planCode = entitlements?.plan || 'legacy_full';
  const plan = catalog?.plans?.find((p) => p.code === planCode);
  const quotas = { ...(entitlements?.quotas || {}) };
  for (const [code, opt] of Object.entries(cart?.quotas || {})) {
    if (!opt || !('value' in opt)) continue;
    const pkg = catalog?.quotaPackages?.find((p) => p.code === code);
    const planDefault = plan?.quotas?.[code];
    const mode = quotaValueMode(pkg);
    quotas[code] = effectiveQuotaFromStoreOption(planDefault, opt.value, mode);
  }
  return { plan: planCode, modules, quotas };
}

/**
 * @returns {{
 *   lines: Array<{ id: string, kind: string, label: string, detail?: string, amount: number }>,
 *   total: number,
 *   cartAdditional?: number,
 *   projectedTotal?: number,
 * }}
 */
export function computeProjectMonthlyCost(catalog, entitlements, cart = null) {
  if (!catalog || !entitlements) {
    return { lines: [], total: 0, cartAdditional: 0, projectedTotal: 0 };
  }

  const current = computeCostForState(catalog, entitlements);
  if (!cart || (!cart.modules?.length && !Object.keys(cart.quotas || {}).length)) {
    return { ...current, cartAdditional: 0, projectedTotal: current.total };
  }

  const projectedEnt = buildSimulatedEntitlements(catalog, entitlements, cart);
  const projected = computeCostForState(catalog, projectedEnt);
  const cartAdditional = Math.max(0, projected.total - current.total);

  return {
    ...current,
    cartAdditional,
    projectedTotal: projected.total,
  };
}

function computeCostForState(catalog, entitlements) {
  const planCode = entitlements?.plan || 'legacy_full';
  const plan = catalog?.plans?.find((p) => p.code === planCode);
  const planModules = new Set(plan?.modules || []);
  const planPrices = catalog?.planPrices || {};
  const planPrice = planPrices[planCode] || { monthly: 0 };

  const lines = [];
  let total = 0;

  if (planPrice.monthly > 0) {
    lines.push({
      id: `plan:${planCode}`,
      kind: 'plan',
      label: plan?.name || planCode,
      detail: 'plan',
      amount: planPrice.monthly,
    });
    total += planPrice.monthly;
  } else if (planCode === 'legacy_full') {
    lines.push({
      id: `plan:${planCode}`,
      kind: 'plan',
      label: plan?.name || planCode,
      detail: planPrice.note || 'legacy',
      amount: 0,
    });
  }

  for (const listing of catalog?.listings || []) {
    const code = listing.code;
    if (!entitlements?.modules?.[code]) continue;
    if (planModules.has(code)) continue;
    const amount = listing.price?.monthly ?? 0;
    if (amount <= 0) continue;
    lines.push({
      id: `addon:${code}`,
      kind: 'addon',
      label: moduleName(catalog, code),
      detail: code,
      amount,
    });
    total += amount;
  }

  for (const pkg of catalog?.quotaPackages || []) {
    const current = entitlements?.quotas?.[pkg.code];
    const planDefault = plan?.quotas?.[pkg.code];
    const amount = quotaAddonMonthlyAmount(pkg, current, planDefault);
    if (amount <= 0) continue;
    const opt = findStoreQuotaOption(pkg, current, planDefault);
    lines.push({
      id: `quota:${pkg.code}`,
      kind: 'quota',
      label: pkg.label,
      detail: opt?.label,
      amount,
    });
    total += amount;
  }

  return { lines, total };
}

export function formatUsd(amount, perMonthLabel = '/ mo') {
  if (amount == null || !Number.isFinite(amount)) return '—';
  if (amount === 0) return '$0';
  return `$${amount}${perMonthLabel}`;
}

/** Dollar amount without a recurring-period suffix (balance, ledger, invoices). */
export function formatMoney(amount) {
  return formatUsd(amount, '');
}
